import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import QuizTaker from "./QuizTaker";
import InvisibleTeacher from "./InvisibleTeacher";
import StandardLessonActions from "./StandardLessonActions";
import { clearLessonProgress } from "../utils/lessonProgress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// =================================================================
// ORDINATION TRACK COURSES - BIBLICAL HEBREW/YISRAELITE PERSPECTIVE
// =================================================================
// All courses grounded in Scripture (Torah, Prophets, Writings, Apostolic texts)
// Distinguished from rabbinic traditions (Mishnah, Talmud)
// Emphasis on identity, covenant, laws, statutes, and restoration of Yisrael

// Pre-seeded study content for each Seminary course
const SEMINARY_STUDY_CONTENT = {
  "bible-study": {
    title: "Bible Study & Scripture - Seminary Level",
    subtitle: "KJV Exegesis from the Hebrew/Yisrael Perspective",
    sections: [
      {
        title: "1. The Authority of Scripture",
        content: `The Holy Scriptures (KJV) are the final authority for doctrine, reproof, correction, and instruction in righteousness (2 Timothy 3:16-17). As ministers of Yisrael, we must distinguish between:

• SCRIPTURE: The Torah (Genesis-Deuteronomy), Prophets (Nevi'im), Writings (Ketuvim), and Apostolic texts
• TRADITION: Later rabbinic writings (Mishnah, Talmud) which often contradict or add to Scripture

"Add thou not unto his words, lest he reprove thee, and thou be found a liar." - Proverbs 30:6`
      },
      {
        title: "2. Reading with Hebrew Eyes",
        content: `The KJV was translated from Hebrew (OT) and Greek (NT). To properly exegete, we must understand:

• YHWH (יהוה) - The personal name of the Creator, replaced with "LORD" in KJV
• Yahushua (יהושע) - The Messiah's Hebrew name, rendered "Jesus" through Greek/Latin
• Qahal (קהל) - Assembly/congregation, rendered "church" (from Greek 'ekklesia')
• Torah (תורה) - Instruction/Law, not abolished but fulfilled (Matthew 5:17-19)

When we read "the LORD," we should understand this is YHWH. When we read "Jesus," we understand this is Yahushua.`
      },
      {
        title: "3. The Covenant Structure",
        content: `Scripture is organized around covenants YHWH made with our fathers:

• ADAMIC COVENANT: Dominion, the promise of the Seed (Genesis 3:15)
• NOAHIC COVENANT: Rainbow sign, no more flood (Genesis 9)
• ABRAHAMIC COVENANT: Land, Seed, Blessing (Genesis 12, 15, 17)
• MOSAIC COVENANT: Torah given at Sinai, conditional blessings/curses (Exodus 19-24)
• DAVIDIC COVENANT: Everlasting throne, Messiah from David's line (2 Samuel 7)
• NEW COVENANT: Torah written on hearts, not abolished but internalized (Jeremiah 31:31-33)

As ministers, we must teach how these covenants connect and remain active for Yisrael.`
      },
      {
        title: "4. Calendar Scriptures",
        content: `Every minister must know the foundational calendar passages:

• Genesis 1:14 - Luminaries for signs, seasons (moedim), days, years
• Exodus 12:2 - "This month shall be unto you the beginning of months"
• Leviticus 23 - The complete feast calendar
• Psalm 104:19 - "He appointed the moon for seasons (moedim)"
• Ezekiel 46:1 - Gate protocol for Sabbaths and New Moons
• Enoch 72-82 - The 364-day solar calendar revealed

The Sun marks years and seasons; the Moon marks months and appointed times.`
      }
    ],
    keyPoints: [
      "Scripture alone is our authority - Sola Scriptura",
      "Understand Hebrew names and concepts behind English translations",
      "Know the covenant structure from Adam to New Covenant",
      "Master all calendar-related Scriptures",
      "Distinguish Scripture from rabbinic tradition"
    ],
    portalDiagram: true
  },
  "theology": {
    title: "Theology & Ministry - Seminary Level",
    subtitle: "Pastoral Care and Sabbath Leadership",
    sections: [
      {
        title: "1. Systematic Theology from Scripture",
        content: `As ministers, we must have a systematic understanding of doctrine grounded ONLY in Scripture:

• THEOLOGY PROPER: YHWH is ONE (Deuteronomy 6:4), Creator of all, Sovereign King
• CHRISTOLOGY: Yahushua is the promised Messiah, Son of YHWH, our High Priest
• PNEUMATOLOGY: The Ruach HaKodesh (Holy Spirit) is YHWH's presence and power
• ANTHROPOLOGY: Man created in YHWH's image, fell through disobedience
• SOTERIOLOGY: Salvation by grace through faith, demonstrated by obedience (James 2:17-26)
• ESCHATOLOGY: The restoration of Yisrael, return of Messiah, Kingdom age

Reject Greco-Roman philosophical concepts that contradict Scripture (Colossians 2:8).`
      },
      {
        title: "2. The 8-15-22-29 Sabbath Protocol",
        content: `As pastor, you will lead your congregation in the lunar Sabbath cycle:

DAY 1: New Moon Day - Rest, worship, gate closed (Ezekiel 46:1)
DAYS 2-7: Work days
DAY 8: First weekly Sabbath - Rest, holy convocation
DAYS 9-14: Work days
DAY 15: Second Sabbath (Full Moon) - Rest, holy convocation
DAYS 16-21: Work days
DAY 22: Third Sabbath - Rest, holy convocation
DAYS 23-28: Work days
DAY 29: Fourth Sabbath - Rest, holy convocation
DAY 30: Work day (some months) or preparation for New Moon

This pattern repeats every month. The gates are OPEN on work days, SHUT on Sabbath and New Moon.`
      },
      {
        title: "3. Pastoral Counseling on Calendar Issues",
        content: `Your congregation will face challenges. Prepare to counsel on:

• EMPLOYER CONFLICTS: Help members draft accommodation requests citing religious freedom
• FAMILY DIVISIONS: When spouses or extended family don't accept the truth
• DOUBT AND UNCERTAINTY: New learners may struggle with such a paradigm shift
• COMMUNITY PRESSURE: Saturday-keeping congregations may reject lunar Sabbath keepers

Always counsel with grace, patience, and Scripture. Don't condemn those still learning.`
      },
      {
        title: "4. Feast Day Leadership",
        content: `The pastor must lead the congregation through the annual feast cycle:

SPRING FEASTS (Month 1):
• Passover (14th) - Memorial service, lamb symbolism
• Unleavened Bread (15th-21st) - Remove leaven, special assemblies
• Firstfruits - Wave offering, resurrection connection

SUMMER FEAST:
• Shavuot/Pentecost (count 50 days) - Wheat harvest, giving of Torah

FALL FEASTS (Month 7):
• Trumpets (1st) - Shofar blowing, prophetic warning
• Atonement (10th) - Fast day, solemn assembly
• Tabernacles (15th-22nd) - Booth dwelling, joyful celebration

Prepare your congregation weeks in advance. Coordinate logistics. Teach the meanings.`
      }
    ],
    keyPoints: [
      "Ground all theology in Scripture, not philosophy",
      "Master the 8-15-22-29 Sabbath protocol",
      "Prepare to counsel on real-world calendar challenges",
      "Lead your congregation through all feast days",
      "Always teach with grace and patience"
    ],
    portalDiagram: true
  },
  "hebrew-identity": {
    title: "Heritage of Yisrael - Seminary Level",
    subtitle: "Identity, Covenant, and the Scattered Nation",
    sections: [
      {
        title: "1. Who Are the True Israelites?",
        content: `Deuteronomy 28 gives us the identifying markers of scattered Yisrael:

• SHIPS TO SLAVERY (v.68): "YHWH shall bring thee into Egypt again with SHIPS" - Only one group was taken to bondage on ships: the Trans-Atlantic Slave Trade
• BYWORD AND PROVERB (v.37): Yisrael would become "an astonishment, a proverb, and a byword" - stereotypes, mockery, slurs
• THE TAIL, NOT HEAD (v.44): "He shall be the head, and thou shalt be the tail" - at the bottom of society, not the top
• SERVE ENEMIES (v.48): "Thou shalt serve thine enemies... in hunger, thirst, nakedness" - describes slavery conditions
• SCATTERED AMONG NATIONS (v.64): "YHWH shall scatter thee among all people" - diaspora worldwide

Match these curses to history. Who fits? The so-called "Black" people of the Americas, Caribbean, and Africa.`
      },
      {
        title: "2. Phenotype Evidence",
        content: `Scripture describes our ancestors' appearance:

• REVELATION 1:14-15: Hair like wool (textured, coiled), feet like burnished brass (dark brown)
• DANIEL 10:6: Arms and feet like polished brass
• SONG OF SOLOMON 1:5-6: "I am black... the sun hath looked upon me"
• LAMENTATIONS 4:8: Visage "blacker than coal"
• ACTS 21:38: Paul mistaken for an Egyptian (Africans)
• EXODUS 2:10, 19: Moses passed as Egyptian and Midianite

The Romans changed the image of the Messiah and the Israelites to European. The true phenotype is bronze skin, wooly hair - like the peoples scattered by the slave trade.`
      },
      {
        title: "3. The 12 Tribes Today",
        content: `Jacob (Yisrael) had 12 sons who became 12 tribes:

LEAH'S SONS: Reuben, Simeon, Levi, Judah, Issachar, Zebulun
RACHEL'S SONS: Joseph (Ephraim & Manasseh), Benjamin
BILHAH'S SONS: Dan, Naphtali
ZILPAH'S SONS: Gad, Asher

These tribes were scattered among the nations (James 1:1 - "to the twelve tribes which are scattered abroad"). Today, the awakening is happening. The descendants of the slave trade are discovering their true identity as Yisrael.

Teaching identity is essential pastoral work. Many have been told they are "nothing" - show them they are royalty!`
      },
      {
        title: "4. The Restoration of Yisrael",
        content: `YHWH promised to regather His people:

• JEREMIAH 29:14: "I will gather you from all the nations, and from all the places whither I have driven you"
• EZEKIEL 37: The dry bones vision - Yisrael will live again
• ISAIAH 11:11-12: "He shall assemble the outcasts of Israel, and gather together the dispersed of Judah"
• DEUTERONOMY 30:1-5: After the curses, YHWH will circumcise hearts and regather

We are living in the time of awakening. The truth is going forth. Ministers must teach the hope of restoration while calling people to repentance and Torah observance.`
      }
    ],
    keyPoints: [
      "Deuteronomy 28 curses identify scattered Yisrael",
      "Phenotype evidence confirms identity",
      "Know the 12 tribes and their history",
      "Teach the promise of restoration",
      "Build identity and dignity in your congregation"
    ],
    portalDiagram: false
  },
  "law": {
    title: "Torah Law & Commandments - Seminary Level",
    subtitle: "The Commandments of YHWH Still Stand",
    sections: [
      {
        title: "1. The Law Is Not Abolished",
        content: `Yahushua said clearly:

"Think not that I am come to destroy the law, or the prophets: I am not come to destroy, but to fulfil. For verily I say unto you, Till heaven and earth pass, one jot or one tittle shall in no wise pass from the law, till all be fulfilled. Whosoever therefore shall break one of these least commandments, and shall teach men so, he shall be called the least in the kingdom of heaven." - Matthew 5:17-19

"Fulfil" (Greek: pleroo) means to fill up, complete, or give full meaning - NOT to abolish. Heaven and earth have not passed. The law stands.`
      },
      {
        title: "2. Categories of Law",
        content: `The Torah contains different types of commandments:

• MORAL LAW: The Ten Commandments, love YHWH and neighbor - eternal principles
• CIVIL LAW: Laws for the nation of Israel - governance, justice, restitution
• CEREMONIAL LAW: Sacrifices, priesthood - some fulfilled in Messiah, temple service suspended
• HEALTH LAWS: Clean/unclean foods (Leviticus 11), hygiene - for our wellbeing

Many teach "the law is done away" but still keep moral laws. Why keep "do not murder" but reject the Sabbath? Both are in the same commandments! We keep what we can in our current scattered state, awaiting the restoration of the temple and priesthood.`
      },
      {
        title: "3. The Sabbath Command",
        content: `The 4th Commandment:

"Remember the sabbath day, to keep it holy. Six days shalt thou labour, and do all thy work: But the seventh day is the sabbath of YHWH thy Elohim: in it thou shalt not do any work..." - Exodus 20:8-10

• The Sabbath is a SIGN between YHWH and Israel (Exodus 31:13-17)
• It is "throughout your generations" - never ended
• It was made for man, not man for the Sabbath (Mark 2:27)
• The lunar Sabbath (8-15-22-29) is the original pattern

As ministers, we must teach and model proper Sabbath keeping. It is the test commandment (Exodus 16).`
      },
      {
        title: "4. The Food Laws",
        content: `YHWH gave dietary instructions (Leviticus 11, Deuteronomy 14):

CLEAN ANIMALS: Split hoof AND chews cud (cattle, sheep, goat, deer)
UNCLEAN: Pig (split hoof but no cud), camel, rabbit, etc.
CLEAN FISH: Fins AND scales
UNCLEAN: Catfish, shellfish, shrimp, crab, lobster
CLEAN BIRDS: Generally seed/plant eaters
UNCLEAN: Birds of prey, scavengers

These laws were not abolished in Acts 10 (Peter's vision was about PEOPLE, not food - see Acts 10:28). They were not abolished in Mark 7 (Yahushua addressed hand-washing traditions, not food laws). Keep YHWH's dietary instructions for health and holiness.`
      }
    ],
    keyPoints: [
      "The Law (Torah) is NOT abolished - Matthew 5:17-19",
      "Understand different categories of laws",
      "The Sabbath is a sign of the covenant",
      "Food laws remain - clean vs unclean",
      "Teach obedience motivated by love, not legalism"
    ],
    portalDiagram: false
  },
  "music": {
    title: "Music & Worship Arts - Seminary Level",
    subtitle: "Leading YHWH's People in Scriptural Worship",
    sections: [
      {
        title: "1. Worship in Spirit and Truth",
        content: `Yahushua said: "The true worshippers shall worship the Father in spirit and in truth: for the Father seeketh such to worship him." - John 4:23

• SPIRIT: Genuine heart engagement, not mere outward ritual
• TRUTH: According to YHWH's instructions, not man's traditions

Worship must be both heartfelt AND obedient. Entertainment-driven worship focuses on how WE feel. True worship focuses on honoring YHWH according to His instructions.`
      },
      {
        title: "2. The Book of Psalms",
        content: `The Psalms are YHWH's worship manual - 150 inspired songs:

• PRAISE PSALMS: Hallel (113-118), Thanksgiving (100, 136)
• LAMENT PSALMS: Crying out to YHWH in trouble (22, 88)
• ROYAL PSALMS: Messianic, kingdom themes (2, 110)
• WISDOM PSALMS: Torah meditation (1, 119)
• IMPRECATORY PSALMS: Prayers against enemies (109, 137)

As worship leaders, we should draw heavily from the Psalms. They cover every human emotion and situation. They are already approved by YHWH!`
      },
      {
        title: "3. Biblical Instruments",
        content: `Scripture mentions specific instruments for worship:

• SHOFAR (Horn): For announcements, feasts, warfare (Numbers 10)
• KINNOR (Harp/Lyre): David played before Saul (1 Samuel 16)
• NEVEL (Psaltery): Larger stringed instrument (Psalm 150)
• TOPH (Tambourine/Drum): Used in celebration (Exodus 15:20)
• HALIL (Flute/Pipe): Wind instrument (1 Kings 1:40)
• CYMBALS: Brass percussion (Psalm 150)

Psalm 150 commands praise with trumpet, psaltery, harp, timbrel, stringed instruments, organs, and cymbals.`
      },
      {
        title: "4. Leading Congregational Worship",
        content: `The worship leader's role:

• PREPARATION: Know the songs, practice, prepare the musicians
• SELECTION: Choose songs appropriate to the service (Sabbath, feast, solemn day)
• HUMILITY: Point people to YHWH, not yourself
• SENSITIVITY: Read the congregation, don't force emotions
• SCRIPTURE: Incorporate reading of Psalms and Scripture

Avoid performance mentality. Worship is not a concert. The congregation should PARTICIPATE, not just watch. Teach them the songs. Lead them in praise.`
      }
    ],
    keyPoints: [
      "Worship must be in Spirit AND Truth",
      "The Psalms are our worship manual",
      "Know biblical instruments and their uses",
      "Lead with humility, not performance",
      "Encourage congregational participation"
    ],
    portalDiagram: false
  }
};

const ORDINATION_COURSES = [
  {
    id: "bible-study",
    name: "Bible Study & Scripture",
    description: "Comprehensive mastery of the Holy Scriptures (KJV) from Biblical Hebrew/Yisrael perspective. Torah, Prophets, Writings, and Apostolic texts as the primary authority. Emphasis on original Hebrew context and proper exegesis.",
    icon: "📖",
    required: true,
    minScore: 80,
    focus: "Scripture as Authority"
  },
  {
    id: "theology",
    name: "Theology & Ministry",
    description: "Systematic theology grounded in Scripture alone. Prophetic studies, Sabbath keeping, Feast Days (Leviticus 23), apologetics, and pastoral ministry. Distinguishes biblical teaching from later rabbinic traditions.",
    icon: "✝️",
    required: true,
    minScore: 80,
    focus: "Scriptural Doctrine"
  },
  {
    id: "hebrew-identity",
    name: "Heritage of Yisrael",
    description: "Deep study of the identity and history of the 12 Tribes based on Scripture. Deuteronomy 28 prophecy, covenant promises, and the restoration of Yisrael. Understanding who we are according to the Word.",
    icon: "✡️",
    required: true,
    minScore: 80,
    focus: "Identity & Covenant"
  },
  {
    id: "law",
    name: "Torah Law & Commandments",
    description: "Comprehensive understanding of Torah law, statutes, and commandments as given by YHWH. The 613 commandments, their application today, and distinction from man-made traditions. The Law not abolished but upheld (Matthew 5:17-19).",
    icon: "⚖️",
    required: true,
    minScore: 80,
    focus: "Laws & Statutes"
  },
  {
    id: "music",
    name: "Music & Worship Arts",
    description: "Worship leadership rooted in Scripture. Psalms study, biblical instruments, leading the congregation in praise according to the patterns established in Scripture. Worship in spirit and truth.",
    icon: "🎵",
    required: false,
    minScore: 70,
    focus: "Scriptural Worship"
  }
];

function OrdinationProgram({ user, config, onOrdinationComplete }) {
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(null);
  const [librarySubjects, setLibrarySubjects] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [takingExam, setTakingExam] = useState(null);
  const [error, setError] = useState("");
  const [showFinalExam, setShowFinalExam] = useState(false);
  const [finalExamTaken, setFinalExamTaken] = useState(false);
  // NEW: Study content view state
  const [studyingCourse, setStudyingCourse] = useState(null);
  const [studyProgress, setStudyProgress] = useState({}); // Track which sections have been read
  const [moonPhase, setMoonPhase] = useState(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // ADMIN AUTHORITY PROTOCOL
  // ═══════════════════════════════════════════════════════════════════════════
  // The Admin (YKBN) is the Creator of the curriculum and has ZERO RESTRICTIONS.
  // - All locks lifted
  // - All certificates auto-granted  
  // - No quiz/prerequisite requirements
  // - Full access to view/edit all content
  // ═══════════════════════════════════════════════════════════════════════════
  const isAdmin = user?.username === "YKBN" || user?.email === "Yisraellearningcenter@gmail.com" || user?.is_admin;

  useEffect(() => {
    fetchOrdinationProgress();
    fetchLibrary();
    fetchMoonPhase();
    
    // ADMIN: Auto-grant all certifications
    if (isAdmin) {
      autoGrantAdminCertifications();
    }
    
    // Load study progress from localStorage
    const savedProgress = localStorage.getItem('seminary_study_progress');
    if (savedProgress) {
      setStudyProgress(JSON.parse(savedProgress));
    }
  }, []);

  // Auto-grant all certifications for Admin
  const autoGrantAdminCertifications = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/ordination/admin-auto-certify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.log("Admin certification check:", err.message);
    }
  };

  const fetchMoonPhase = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/celestial/moon-phase`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMoonPhase(response.data);
    } catch (err) {
      console.log("Moon phase fetch error:", err);
    }
  };

  const fetchOrdinationProgress = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ordination/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgress(response.data);
      setFinalExamTaken(response.data.final_exam_passed || false);
    } catch (err) {
      console.error("Error fetching ordination progress:", err);
      // Initialize with empty progress if endpoint doesn't exist yet
      setProgress({
        courses_completed: [],
        courses_in_progress: [],
        course_scores: {},
        eligible_for_ordination: false,
        final_exam_passed: false
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLibrary = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/library`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLibrarySubjects(response.data.subjects || []);
    } catch (err) {
      console.error("Error fetching library:", err);
    }
  };

  const isCourseInLibrary = (courseId) => {
    // Check if the subject exists AND has Ordination level content
    const subject = librarySubjects.find(s => s.subject_id === courseId);
    if (!subject) return false;
    
    // Check if Ordination grade level exists and has lessons
    const ordinationContent = subject.grade_levels?.["Ordination"];
    return ordinationContent && ordinationContent.lessons && ordinationContent.lessons.length > 0;
  };

  const getCourseStatus = (courseId) => {
    // ADMIN AUTHORITY: All courses show as completed
    if (isAdmin) return "completed";
    
    if (!progress) return "not_started";
    
    const courseScore = progress.course_scores?.[courseId];
    const course = ORDINATION_COURSES.find(c => c.id === courseId);
    const requiredScore = course?.minScore || 80;
    
    if (courseScore && courseScore >= requiredScore) {
      return "completed";
    } else if (courseScore) {
      return "in_progress";
    }
    return "not_started";
  };

  const getCompletedCount = () => {
    if (!progress) return 0;
    return ORDINATION_COURSES.filter(c => 
      c.required && getCourseStatus(c.id) === "completed"
    ).length;
  };

  const getTotalRequired = () => {
    return ORDINATION_COURSES.filter(c => c.required).length;
  };

  const isEligibleForFinalExam = () => {
    const requiredCourses = ORDINATION_COURSES.filter(c => c.required);
    return requiredCourses.every(c => getCourseStatus(c.id) === "completed");
  };

  // Check if all sections of a course have been studied
  // ADMIN: Always returns true (no restrictions)
  const hasStudiedCourse = (courseId) => {
    if (isAdmin) return true; // ADMIN BYPASS
    const content = SEMINARY_STUDY_CONTENT[courseId];
    if (!content) return false;
    const courseProgress = studyProgress[courseId] || {};
    return content.sections.every((_, idx) => courseProgress[idx]);
  };

  // Mark a section as read
  const markSectionRead = (courseId, sectionIndex) => {
    const newProgress = {
      ...studyProgress,
      [courseId]: {
        ...studyProgress[courseId],
        [sectionIndex]: true
      }
    };
    setStudyProgress(newProgress);
    localStorage.setItem('seminary_study_progress', JSON.stringify(newProgress));
  };

  // Get study progress percentage for a course
  const getStudyProgressPercent = (courseId) => {
    const content = SEMINARY_STUDY_CONTENT[courseId];
    if (!content) return 0;
    const courseProgress = studyProgress[courseId] || {};
    const readCount = content.sections.filter((_, idx) => courseProgress[idx]).length;
    return Math.round((readCount / content.sections.length) * 100);
  };

  // Open study content view
  const handleStudyCourse = (course) => {
    setStudyingCourse(course);
    setError("");
  };

  const handleStartCourseExam = (course) => {
    // REQUIRE study completion before exam
    if (!hasStudiedCourse(course.id)) {
      setError(`You must complete the study material for ${course.name} before taking the exam. Click "Study Material" to begin.`);
      return;
    }
    
    setTakingExam({
      subjectId: course.id,
      subjectName: course.name,
      isOrdination: true,
      minScore: course.minScore
    });
    setError("");
  };

  const handleExamComplete = async (result) => {
    // Refresh progress after exam
    await fetchOrdinationProgress();
    setTakingExam(null);
  };

  const handleFinalExam = () => {
    setShowFinalExam(true);
  };

  const handleFinalExamComplete = async (result) => {
    if (result.passed) {
      // Call backend to mark user as ordained
      try {
        const token = localStorage.getItem("token");
        await axios.post(`${API}/ordination/complete`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setFinalExamTaken(true);
        if (onOrdinationComplete) {
          onOrdinationComplete();
        }
      } catch (err) {
        console.error("Error completing ordination:", err);
      }
    }
    setShowFinalExam(false);
    await fetchOrdinationProgress();
  };

  // =================================================================
  // STUDY CONTENT VIEW - Seminary Material with Portal Diagrams
  // =================================================================
  // ── 2026-02-11d ORDINATION TRACK UPGRADE ─────────────────────────
  // The Ordination study view was the only major lesson surface that
  // lacked the platform's standard InvisibleTeacher + 5-button action
  // bar. User mandate: "Ordination track should have invisible teacher
  // and navigation buttons, same as all other courses." We add a
  // body-wrapping ref (for PDF export), a restart key, lazy per-section
  // AI deep-expansion, and the full standardized action bar.
  const [ordRestartKey, setOrdRestartKey] = useState(0);
  const [ordPdfLoading, setOrdPdfLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({}); // { [sectionIdx]: "expanded text" }
  const [expandingIdx, setExpandingIdx] = useState(null);
  const ordBodyRef = useRef(null);

  // Concatenated lesson text for InvisibleTeacher. Falls back to the
  // pre-seeded content; when a section has been AI-expanded, the
  // expanded text replaces the seed.
  const ordReadingScript = useMemo(() => {
    if (!studyingCourse) return "";
    const c = SEMINARY_STUDY_CONTENT[studyingCourse.id];
    if (!c) return "";
    return [
      `${c.title}. ${c.subtitle || ""}`,
      ...c.sections.map((s, idx) => {
        const exp = expandedSections[idx];
        return `${s.title}\n\n${exp || s.content}`;
      }),
      c.keyPoints ? `Key Points: ${c.keyPoints.join("; ")}` : "",
    ]
      .filter(Boolean)
      .join("\n\n");
  }, [studyingCourse, expandedSections]);

  const handleOrdExpandSection = useCallback(async (idx, sectionText) => {
    if (!studyingCourse) return;
    setExpandingIdx(idx);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API}/classroom/voice-first/explain`,
        {
          topic: sectionText,
          grade_level: "Ordination",
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      const expanded =
        res.data?.explanation_text ||
        res.data?.explanation ||
        res.data?.expanded_text ||
        res.data?.content;
      if (expanded) {
        // Defensive: keep React's render path safe even if a future
        // change returns a non-string. See React #31 RCA in
        // SpecialEdition iter37.
        const safe =
          typeof expanded === "string" ? expanded : JSON.stringify(expanded, null, 2);
        setExpandedSections((prev) => ({ ...prev, [idx]: safe }));
      } else {
        alert("AI deep-expansion returned no content — please try again.");
      }
    } catch (e) {
      console.error("Deep-expand failed:", e);
      alert("Couldn't expand this section right now — please try again shortly.");
    } finally {
      setExpandingIdx(null);
    }
  }, [studyingCourse]);

  const handleOrdRestart = useCallback(() => {
    const lessonTitle =
      (studyingCourse &&
        (SEMINARY_STUDY_CONTENT[studyingCourse.id]?.title || studyingCourse.name)) ||
      "";
    clearLessonProgress(lessonTitle);
    setOrdRestartKey((k) => k + 1);
    setExpandedSections({});
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [studyingCourse]);

  const handleOrdDownloadPDF = useCallback(async () => {
    if (!studyingCourse) return;
    setOrdPdfLoading(true);
    try {
      const { exportLessonPDF } = await import("../utils/pdfExport");
      const title = SEMINARY_STUDY_CONTENT[studyingCourse.id]?.title || studyingCourse.name;
      await exportLessonPDF({
        title,
        subtitle: `Ordination Track · ${studyingCourse.name}`,
        bodyNode: ordBodyRef.current,
        studentName: user?.full_name || user?.username,
        gradeLevel: "Ordination",
      });
    } catch (e) {
      console.error("Ordination PDF export failed:", e);
      alert("PDF export failed. Please try again.");
    } finally {
      setOrdPdfLoading(false);
    }
  }, [studyingCourse, user]);

  if (studyingCourse) {
    const content = SEMINARY_STUDY_CONTENT[studyingCourse.id];
    const courseProgress = studyProgress[studyingCourse.id] || {};
    const allRead = content?.sections.every((_, idx) => courseProgress[idx]);
    
    return (
      <div data-testid="seminary-study-view" style={{ maxWidth: "900px", margin: "0 auto" }}>
        <div ref={ordBodyRef} key={ordRestartKey}>
        {/* Header */}
        <div className="card" style={{ 
          background: "linear-gradient(135deg, #1a1a2a 0%, #2a1a3a 100%)",
          borderTop: "4px solid #FFD700"
        }}>
          <button
            onClick={() => setStudyingCourse(null)}
            style={{ background: "transparent", border: "none", color: "#aaa", cursor: "pointer", marginBottom: "15px" }}
          >
            ← Back to Ordination Program
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <span style={{ fontSize: "3rem" }}>{studyingCourse.icon}</span>
            <div>
              <h2 style={{ color: "#FFD700", margin: 0 }}>{content?.title || studyingCourse.name}</h2>
              <p style={{ color: "#aaa", margin: "5px 0 0" }}>{content?.subtitle || "Seminary Study Material"}</p>
            </div>
          </div>
          
          {/* Study Progress Bar */}
          <div style={{ marginTop: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
              <span style={{ color: "#aaa", fontSize: "0.9rem" }}>Study Progress</span>
              <span style={{ color: "#FFD700", fontSize: "0.9rem" }}>{getStudyProgressPercent(studyingCourse.id)}%</span>
            </div>
            <div style={{ background: "#111", borderRadius: "10px", padding: "2px" }}>
              <div style={{
                background: allRead ? "linear-gradient(90deg, #90EE90 0%, #32CD32 100%)" : "linear-gradient(90deg, #FFD700 0%, #FFA500 100%)",
                height: "10px",
                borderRadius: "8px",
                width: `${getStudyProgressPercent(studyingCourse.id)}%`,
                transition: "width 0.3s ease"
              }} />
            </div>
          </div>
        </div>

        {/* Moon Phase & Portal Data Card */}
        {content?.portalDiagram && (
          <div className="card" style={{ background: "#111", borderLeft: "4px solid #87CEEB" }}>
            <h4 style={{ color: "#87CEEB", marginBottom: "15px" }}>🌙 Current Celestial Data</h4>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px" }}>
              <div style={{ background: "#1a1a2a", padding: "15px", borderRadius: "8px" }}>
                <div style={{ color: "#aaa", fontSize: "0.85rem" }}>Moon Phase</div>
                <div style={{ color: "#FFD700", fontSize: "1.3rem", marginTop: "5px" }}>
                  {moonPhase?.phase || "Waxing"} {moonPhase?.illumination ? `(${Math.round(moonPhase.illumination * 100)}%)` : ""}
                </div>
                <div style={{ color: "#888", fontSize: "0.8rem", marginTop: "5px" }}>
                  Day {moonPhase?.day_of_month || "?"} of Month
                </div>
              </div>
              <div style={{ background: "#1a1a2a", padding: "15px", borderRadius: "8px" }}>
                <div style={{ color: "#aaa", fontSize: "0.85rem" }}>Sabbath Status</div>
                <div style={{ color: moonPhase?.is_sabbath ? "#90EE90" : "#FFD700", fontSize: "1.3rem", marginTop: "5px" }}>
                  {moonPhase?.is_sabbath ? "SABBATH" : moonPhase?.is_new_moon ? "NEW MOON" : "Work Day"}
                </div>
                <div style={{ color: "#888", fontSize: "0.8rem", marginTop: "5px" }}>
                  Next Sabbath: Day {moonPhase?.next_sabbath || "?"}
                </div>
              </div>
            </div>

            {/* Portal Diagram */}
            <div style={{ marginTop: "20px", padding: "20px", background: "#0a0a15", borderRadius: "8px" }}>
              <h5 style={{ color: "#FFD700", textAlign: "center", marginBottom: "15px" }}>The 6 Gates of the Sun (Enoch 72)</h5>
              <div style={{ 
                display: "flex", 
                justifyContent: "space-around", 
                alignItems: "center",
                flexWrap: "wrap",
                gap: "10px"
              }}>
                {[1, 2, 3, 4, 5, 6].map(gate => (
                  <div key={gate} style={{
                    background: gate === 4 ? "#4d3a00" : "#1a1a2a",
                    border: gate === 4 ? "2px solid #FFD700" : "1px solid #333",
                    padding: "10px 15px",
                    borderRadius: "8px",
                    textAlign: "center",
                    minWidth: "80px"
                  }}>
                    <div style={{ color: gate === 4 ? "#FFD700" : "#888", fontSize: "0.75rem" }}>Gate</div>
                    <div style={{ color: gate === 4 ? "#FFD700" : "#fff", fontSize: "1.5rem", fontWeight: "bold" }}>{gate}</div>
                    <div style={{ color: "#666", fontSize: "0.65rem" }}>
                      {gate === 1 ? "Winter" : gate === 4 ? "Equinox" : gate === 6 ? "Summer" : ""}
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ color: "#666", textAlign: "center", fontSize: "0.8rem", marginTop: "15px", marginBottom: 0 }}>
                The Sun rises through Gate 4 at the Equinoxes. Gate 6 = Summer Solstice. Gate 1 = Winter Solstice.
              </p>
            </div>
          </div>
        )}

        {/* Study Sections */}
        {content?.sections.map((section, idx) => (
          <div 
            key={idx}
            className="card"
            style={{
              background: courseProgress[idx] ? "#1a2a1a" : "#1a1a1a",
              borderLeft: `4px solid ${courseProgress[idx] ? "#90EE90" : "#666"}`
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "15px" }}>
              <h4 style={{ color: courseProgress[idx] ? "#90EE90" : "#FFD700", margin: 0 }}>
                {courseProgress[idx] && "✓ "}{section.title}
              </h4>
              {!courseProgress[idx] && (
                <button
                  onClick={() => markSectionRead(studyingCourse.id, idx)}
                  style={{
                    background: "#FFD700",
                    color: "#000",
                    padding: "5px 12px",
                    fontSize: "0.8rem",
                    borderRadius: "4px"
                  }}
                >
                  Mark as Read
                </button>
              )}
            </div>
            <div style={{ 
              color: "#ddd", 
              lineHeight: "1.8", 
              whiteSpace: "pre-wrap",
              fontSize: "0.95rem"
            }}>
              {expandedSections[idx] || section.content}
            </div>
            {/* AI Deep-Expand control — pulls a richer seminary-depth
                expansion of this section on demand. The expanded text
                replaces the seed inline AND feeds the InvisibleTeacher
                so the audio narration uses the deeper material. */}
            <div style={{ marginTop: "12px" }}>
              <button
                onClick={() => handleOrdExpandSection(idx, section.content)}
                disabled={expandingIdx === idx || !!expandedSections[idx]}
                data-testid={`ord-expand-section-${idx}`}
                style={{
                  background: expandedSections[idx] ? "#1a2a1a" : "linear-gradient(135deg, #2a1a3a 0%, #4a2a5a 100%)",
                  color: expandedSections[idx] ? "#90EE90" : "#DDA0DD",
                  border: "1px solid #DDA0DD",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  fontSize: "0.85rem",
                  cursor: expandingIdx === idx ? "wait" : (expandedSections[idx] ? "default" : "pointer"),
                }}
              >
                {expandingIdx === idx
                  ? "Expanding…"
                  : expandedSections[idx]
                  ? "✓ Expanded with AI"
                  : "📖 Deep-Expand with AI"}
              </button>
            </div>
          </div>
        ))}

        {/* Key Points Summary */}
        {content?.keyPoints && (
          <div className="card" style={{ background: "#1a1a3a", borderLeft: "4px solid #87CEEB" }}>
            <h4 style={{ color: "#87CEEB", marginBottom: "15px" }}>📌 Key Points to Remember</h4>
            <ul style={{ margin: 0, paddingLeft: "20px" }}>
              {content.keyPoints.map((point, idx) => (
                <li key={idx} style={{ color: "#ddd", marginBottom: "10px", lineHeight: "1.6" }}>{point}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Exam Button */}
        <div className="card" style={{ textAlign: "center", background: allRead ? "#1a2a1a" : "#1a1a1a" }}>
          {allRead ? (
            <>
              <div style={{ fontSize: "3rem", marginBottom: "15px" }}>✅</div>
              <h4 style={{ color: "#90EE90" }}>Study Complete!</h4>
              <p style={{ color: "#aaa", marginBottom: "20px" }}>
                You have completed all study material for {studyingCourse.name}. You may now take the exam.
              </p>
              <button
                onClick={() => {
                  setStudyingCourse(null);
                  handleStartCourseExam(studyingCourse);
                }}
                style={{
                  background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                  color: "#000",
                  padding: "15px 40px",
                  fontSize: "1.1rem",
                  fontWeight: "bold"
                }}
              >
                📝 Take Exam Now
              </button>
            </>
          ) : (
            <>
              <div style={{ fontSize: "3rem", marginBottom: "15px" }}>📖</div>
              <h4 style={{ color: "#FFD700" }}>Continue Studying</h4>
              <p style={{ color: "#aaa" }}>
                Read all sections above and mark them as read to unlock the exam.
              </p>
              <p style={{ color: "#666", fontSize: "0.9rem" }}>
                {content?.sections.filter((_, idx) => courseProgress[idx]).length || 0} of {content?.sections.length} sections completed
              </p>
            </>
          )}
        </div>
        </div>

        {/* THE INVISIBLE TEACHER — Voice-First AI for Ordination.
            2026-02-11d user mandate: parity with K-12 / Courses /
            Living School / Special Edition. Reads the full study
            script (including any AI-expanded sections). */}
        <InvisibleTeacher
          key={`ord-it-${studyingCourse.id}-${ordRestartKey}`}
          gradeLevel="Ordination"
          lessonContent={ordReadingScript}
          lessonTitle={SEMINARY_STUDY_CONTENT[studyingCourse.id]?.title || studyingCourse.name}
          user={user}
          onSectionStart={(idx) => {
            try {
              window.dispatchEvent(
                new CustomEvent("ylc:teacher-section-start", { detail: { idx } })
              );
            } catch {
              /* non-fatal */
            }
          }}
        />

        {/* Standardized 5-button lesson action bar — same set the
            student sees in K-12 Academic Foundations, Courses, Zion
            Pillars, and Living School. */}
        <StandardLessonActions
          onBack={() => setStudyingCourse(null)}
          onRestart={handleOrdRestart}
          onTakeQuiz={() => {
            setStudyingCourse(null);
            handleStartCourseExam(studyingCourse);
          }}
          onDownloadPDF={handleOrdDownloadPDF}
          pdfLoading={ordPdfLoading}
        />
      </div>
    );
  }

  // Show QuizTaker if taking an exam
  if (takingExam) {
    return (
      <QuizTaker
        subjectId={takingExam.subjectId}
        gradeLevel="Ordination"
        lessonNumber={null}
        isExam={true}
        isOrdination={true}
        onComplete={handleExamComplete}
        onCancel={() => setTakingExam(null)}
      />
    );
  }

  // Show Final Ordination Exam
  if (showFinalExam) {
    return (
      <div className="card" data-testid="ordination-final-exam">
        <h3 style={{ color: "#FFD700", textAlign: "center" }}>
          ✝️ Final Ordination Examination
        </h3>
        <p style={{ textAlign: "center", color: "#aaa", marginBottom: "20px" }}>
          Comprehensive exam covering all ordination courses. Requires 85% to pass.
        </p>
        
        <QuizTaker
          subjectId="ordination-final"
          gradeLevel="Ordination"
          lessonNumber={null}
          isExam={true}
          isOrdination={true}
          isFinalOrdination={true}
          onComplete={handleFinalExamComplete}
          onCancel={() => setShowFinalExam(false)}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="card" data-testid="ordination-loading">
        <h3>✝️ Ordination Program</h3>
        <div className="spinner"></div>
        <p style={{ textAlign: "center", color: "#aaa" }}>Loading ordination progress...</p>
      </div>
    );
  }

  // Check if user is already ordained
  if (user?.ordained || progress?.ordained) {
    return (
      <div className="card" data-testid="ordination-complete">
        <div style={{ textAlign: "center", padding: "30px" }}>
          <div style={{ fontSize: "5rem", marginBottom: "20px" }}>🎓</div>
          <h2 style={{ color: "#FFD700", marginBottom: "15px" }}>
            Congratulations, Minister {user.username}!
          </h2>
          <p style={{ color: "#90EE90", fontSize: "1.2rem", marginBottom: "20px" }}>
            You have been ordained by the {config?.CHURCH_NAME || "Twelve Tribes of Yisrael"}
          </p>
          <div style={{
            background: "#1a2a1a",
            padding: "20px",
            borderRadius: "8px",
            borderLeft: "4px solid #90EE90"
          }}>
            <p style={{ color: "#eee", margin: 0 }}>
              Your ordination certificate is available in the <strong>Certificate</strong> section.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="ordination-program">
      {/* Header */}
      <div className="card" style={{ 
        background: "linear-gradient(135deg, #1a1a1a 0%, #2a1a2a 100%)",
        borderTop: "4px solid #FFD700"
      }}>
        <div style={{ textAlign: "center" }}>
          <h2 style={{ color: "#FFD700", marginBottom: "10px" }}>
            ✝️ Ordination Program
          </h2>
          <p style={{ color: "#aaa", marginBottom: "20px" }}>
            {config?.CHURCH_NAME || "Twelve Tribes of Yisrael"} Ministry Ordination Track
          </p>
          
          {/* Progress Bar */}
          <div style={{
            background: "#111",
            borderRadius: "10px",
            padding: "3px",
            marginBottom: "15px"
          }}>
            <div style={{
              background: "linear-gradient(90deg, #FFD700 0%, #90EE90 100%)",
              height: "20px",
              borderRadius: "8px",
              width: `${(getCompletedCount() / getTotalRequired()) * 100}%`,
              transition: "width 0.5s ease"
            }} />
          </div>
          
          <p style={{ color: "#FFD700", fontWeight: "bold" }}>
            {getCompletedCount()} of {getTotalRequired()} Required Courses Completed
          </p>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Ordination Requirements Info */}
      <div className="card" style={{ background: "#1a1a2a", borderLeft: "4px solid #87CEEB" }}>
        <h4 style={{ color: "#87CEEB", marginBottom: "15px" }}>
          📋 Ordination Requirements
        </h4>
        <ul style={{ margin: 0, paddingLeft: "20px", color: "#aaa" }}>
          <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
            Complete all <strong style={{ color: "#FFD700" }}>4 required courses</strong> at Ordination level
          </li>
          <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
            Score at least <strong style={{ color: "#90EE90" }}>80%</strong> on each required course exam
          </li>
          <li style={{ marginBottom: "8px", background: "transparent", border: "none", padding: "0" }}>
            Pass the <strong style={{ color: "#FFD700" }}>Final Ordination Examination</strong> with 85% or higher
          </li>
          <li style={{ background: "transparent", border: "none", padding: "0" }}>
            Receive your official <strong style={{ color: "#FFD700" }}>Ordination Certificate</strong>
          </li>
        </ul>
      </div>

      {/* Course Cards */}
      <div className="card">
        <h4 style={{ color: "#FFD700", marginBottom: "20px" }}>
          📚 Ordination Courses
        </h4>

        <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
          {ORDINATION_COURSES.map((course) => {
            const status = getCourseStatus(course.id);
            const inLibrary = isCourseInLibrary(course.id);
            const score = progress?.course_scores?.[course.id];

            return (
              <div
                key={course.id}
                data-testid={`ordination-course-${course.id}`}
                style={{
                  background: status === "completed" ? "#1a2a1a" : "#222",
                  padding: "20px",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${
                    status === "completed" ? "#90EE90" : 
                    status === "in_progress" ? "#FFD700" : "#666"
                  }`
                }}
              >
                <div style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: "15px"
                }}>
                  <div style={{ flex: 1, minWidth: "250px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                      <span style={{ fontSize: "2rem" }}>{course.icon}</span>
                      <div>
                        <h4 style={{ color: "#FFD700", margin: 0 }}>
                          {course.name}
                        </h4>
                        <div style={{ display: "flex", gap: "8px", marginTop: "5px" }}>
                          {course.required && (
                            <span style={{
                              background: "#8B0000",
                              color: "#FFD700",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              fontSize: "0.75rem"
                            }}>
                              REQUIRED
                            </span>
                          )}
                          <span style={{
                            background: "#1a1a1a",
                            color: "#87CEEB",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontSize: "0.75rem"
                          }}>
                            Min Score: {course.minScore}%
                          </span>
                        </div>
                      </div>
                    </div>
                    <p style={{ margin: 0, color: "#aaa", fontSize: "0.9rem" }}>
                      {course.description}
                    </p>

                    {/* Status Badges */}
                    <div style={{ marginTop: "10px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      {!inLibrary && (
                        <span style={{
                          background: "#4d1a1a",
                          color: "#ff6b6b",
                          padding: "4px 10px",
                          borderRadius: "4px",
                          fontSize: "0.8rem"
                        }}>
                          ⚠️ Generate Curriculum First
                        </span>
                      )}
                      {inLibrary && status === "not_started" && !hasStudiedCourse(course.id) && (
                        <span style={{
                          background: "#1a1a3a",
                          color: "#87CEEB",
                          padding: "4px 10px",
                          borderRadius: "4px",
                          fontSize: "0.8rem"
                        }}>
                          📖 Study Material Available
                        </span>
                      )}
                      {inLibrary && status === "not_started" && hasStudiedCourse(course.id) && (
                        <span style={{
                          background: "#2a3a1a",
                          color: "#90EE90",
                          padding: "4px 10px",
                          borderRadius: "4px",
                          fontSize: "0.8rem"
                        }}>
                          ✓ Study Complete - Ready for Exam
                        </span>
                      )}
                      {status === "in_progress" && (
                        <span style={{
                          background: "#2a2a1a",
                          color: "#FFD700",
                          padding: "4px 10px",
                          borderRadius: "4px",
                          fontSize: "0.8rem"
                        }}>
                          📝 Score: {score}% (Need {course.minScore}%)
                        </span>
                      )}
                      {status === "completed" && (
                        <span style={{
                          background: "#1a4d1a",
                          color: "#90EE90",
                          padding: "4px 10px",
                          borderRadius: "4px",
                          fontSize: "0.8rem"
                        }}>
                          ✓ Completed ({score}%)
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ 
                    display: "flex", 
                    flexDirection: "column", 
                    gap: "10px",
                    minWidth: "160px"
                  }}>
                    {/* Study Material Button - Always show for unfinished courses */}
                    {status !== "completed" && SEMINARY_STUDY_CONTENT[course.id] && (
                      <button
                        onClick={() => handleStudyCourse(course)}
                        data-testid={`study-ordination-${course.id}`}
                        style={{
                          marginTop: 0,
                          background: hasStudiedCourse(course.id) ? "#1a4d1a" : "#4169E1",
                          color: "#fff",
                        }}
                      >
                        {hasStudiedCourse(course.id) ? "✓ Review Material" : "📖 Study Material"}
                      </button>
                    )}
                    
                    {/* Take Exam Button - Only active after study complete */}
                    {status !== "completed" && (
                      <button
                        onClick={() => handleStartCourseExam(course)}
                        disabled={!hasStudiedCourse(course.id)}
                        data-testid={`take-ordination-exam-${course.id}`}
                        style={{
                          marginTop: 0,
                          background: hasStudiedCourse(course.id) ? "#FFD700" : "#333",
                          color: hasStudiedCourse(course.id) ? "#000" : "#666",
                          opacity: hasStudiedCourse(course.id) ? 1 : 0.6,
                          cursor: hasStudiedCourse(course.id) ? "pointer" : "not-allowed"
                        }}
                      >
                        {!hasStudiedCourse(course.id) ? "🔒 Study First" : status === "in_progress" ? "📝 Retake Exam" : "📝 Take Exam"}
                      </button>
                    )}
                    {status === "completed" && (
                      <button
                        disabled
                        style={{
                          marginTop: 0,
                          background: "#1a4d1a",
                          color: "#90EE90",
                          cursor: "default"
                        }}
                      >
                        ✓ Course Passed
                      </button>
                    )}
                    
                    {/* Study Progress Indicator */}
                    {status !== "completed" && SEMINARY_STUDY_CONTENT[course.id] && (
                      <div style={{ 
                        background: "#111", 
                        borderRadius: "4px", 
                        padding: "5px 10px",
                        fontSize: "0.75rem",
                        color: "#888",
                        textAlign: "center"
                      }}>
                        Study: {getStudyProgressPercent(course.id)}%
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Final Ordination Exam Section */}
      <div className="card" style={{ 
        background: isEligibleForFinalExam() ? 
          "linear-gradient(135deg, #1a2a1a 0%, #2a3a1a 100%)" : 
          "#1a1a1a",
        borderTop: `4px solid ${isEligibleForFinalExam() ? "#90EE90" : "#666"}`
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "4rem", marginBottom: "15px" }}>
            {isEligibleForFinalExam() ? "🎓" : "🔒"}
          </div>
          <h3 style={{ color: isEligibleForFinalExam() ? "#90EE90" : "#666" }}>
            Final Ordination Examination
          </h3>
          
          {!isEligibleForFinalExam() ? (
            <div>
              <p style={{ color: "#aaa", marginBottom: "15px" }}>
                Complete all required courses with 80%+ to unlock the final exam.
              </p>
              <div style={{
                background: "#111",
                padding: "15px",
                borderRadius: "8px",
                display: "inline-block"
              }}>
                <p style={{ margin: 0, color: "#FFD700" }}>
                  {getCompletedCount()} / {getTotalRequired()} courses completed
                </p>
              </div>
            </div>
          ) : (
            <div>
              <p style={{ color: "#90EE90", marginBottom: "20px" }}>
                You are eligible for the Final Ordination Examination!
              </p>
              <p style={{ color: "#aaa", marginBottom: "20px" }}>
                This comprehensive exam covers all ordination courses.
                You must score <strong style={{ color: "#FFD700" }}>85% or higher</strong> to be ordained.
              </p>
              <button
                onClick={handleFinalExam}
                data-testid="take-final-ordination-exam"
                style={{
                  background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                  color: "#000",
                  padding: "15px 40px",
                  fontSize: "1.1rem",
                  fontWeight: "bold"
                }}
              >
                ✝️ Begin Final Ordination Exam
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Ordination Transcript */}
      <div className="card">
        <h4 style={{ color: "#FFD700", marginBottom: "15px" }}>
          📜 Ordination Transcript
        </h4>
        
        {getCompletedCount() === 0 ? (
          <p style={{ color: "#aaa", textAlign: "center" }}>
            No ordination exams completed yet. Start with the required courses above.
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Course</th>
                <th>Score</th>
                <th>Status</th>
                <th>Requirement</th>
              </tr>
            </thead>
            <tbody>
              {ORDINATION_COURSES.filter(c => c.required).map((course) => {
                const score = progress?.course_scores?.[course.id];
                const status = getCourseStatus(course.id);
                
                return (
                  <tr key={course.id}>
                    <td>
                      {course.icon} {course.name}
                    </td>
                    <td>
                      <strong style={{ 
                        color: status === "completed" ? "#90EE90" : 
                               status === "in_progress" ? "#FFD700" : "#666"
                      }}>
                        {score ? `${score}%` : "—"}
                      </strong>
                    </td>
                    <td>
                      <span style={{
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "0.8rem",
                        background: status === "completed" ? "#1a4d1a" : 
                                   status === "in_progress" ? "#2a2a1a" : "#1a1a1a",
                        color: status === "completed" ? "#90EE90" : 
                               status === "in_progress" ? "#FFD700" : "#666"
                      }}>
                        {status === "completed" ? "PASSED" : 
                         status === "in_progress" ? "NEEDS RETAKE" : "NOT TAKEN"}
                      </span>
                    </td>
                    <td style={{ color: "#aaa" }}>
                      Min {course.minScore}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default OrdinationProgram;
